import React = require("react");
import { LanguageCode, stringUtil } from '../../../../../../../amorg-common';
import { createSurveyTheme, moveSurveyQuestion, moveSurveyTheme } from "../../../../../../../api/surveyClientApi";
import { Survey, getAllIndicators, surveyHelper, getSurveyStatus } from '../../../../../../../amorg-common';
import { Flex } from "../../../../../../../designSystem/compositeComponents/FlexHelpers";
import { DndContext, DragEndEvent } from "@dnd-kit/core";
import { PageSection } from "../../../../../../../designSystem/PageSection";
import { ThemedQuestionList } from "./ThemedQuestionList";

type ThemeListProps = {
    survey: Survey,
    languageCode:LanguageCode,
    onSurveyUpdated: () => void,
}

export function ThemeList(props: ThemeListProps): JSX.Element {

    async function handleClickAddNewTheme() {
        const name = stringUtil.generateNextSequenceValue('Nyt tema', props.survey.themes.map(t => t.name));
        if (await createSurveyTheme(props.survey.id, name))
            props.onSurveyUpdated();
    }

    async function handleDragEnd(event: DragEndEvent) {
        const activeId = event.active.id.toString();
        const overId = event.over?.id.toString();

        if (!overId || activeId === overId) {
            return;
        }

        if (activeId.startsWith('theme_') && overId.startsWith('theme_')) {
            const fromThemeIndex = parseInt(activeId.substring(6));
            const toThemeIndex = parseInt(overId.substring(6));

            if (fromThemeIndex !== toThemeIndex) {
                const result = await moveSurveyTheme(props.survey.id, fromThemeIndex, toThemeIndex);
                if (result.isSuccess) {
                    props.onSurveyUpdated();
                }
            }

            return;
        }

        if (!activeId.startsWith('question_')) {
            return;
        }

        const [fromThemeIndex, fromQuestionIndex] = activeId.substring(9).split('_').map(value => parseInt(value));

        let toThemeIndex: number | null = null;
        let toQuestionIndex: number | null = null;

        if (overId.startsWith('question_')) {
            const parsed = overId.substring(9).split('_').map(value => parseInt(value));
            toThemeIndex = parsed[0];
            toQuestionIndex = parsed[1];
        }

        if (overId.startsWith('theme_questions_')) {
            toThemeIndex = parseInt(overId.substring(16));
            const destinationQuestions = props.survey.themes[toThemeIndex]?.questions ?? [];
            toQuestionIndex = fromThemeIndex === toThemeIndex
                ? Math.max(destinationQuestions.length - 1, 0)
                : destinationQuestions.length;
        }

        if (toThemeIndex === null || toQuestionIndex === null) {
            return;
        }

        if (fromThemeIndex === toThemeIndex && fromQuestionIndex === toQuestionIndex) {
            return;
        }

        const result = await moveSurveyQuestion(
            props.survey.id,
            fromThemeIndex,
            fromQuestionIndex,
            toThemeIndex,
            toQuestionIndex
        );

        if (result.isSuccess) {
            props.onSurveyUpdated();
        }
    }

    const allIndicators = getAllIndicators(props.survey);
    const disableEdit = !getSurveyStatus(props.survey).canEdit;

    return (
        <DndContext onDragEnd={handleDragEnd}>

            <PageSection
                headerStyleVariant='card'
                contentStyleVariant='card'
                header={{
                    icon: "quiz",
                    title:'Spørgsmål fordelt på tema',
                    avatar: { text: surveyHelper.getAllSurveyQuestions(props.survey).length.toString() },
                    defaultCollapsed: false,
                    button:{
                        variant:"outlined",
                        onClick:handleClickAddNewTheme,
                        icon:"actions_plus",
                        text:"Opret tema",
                        disabled:disableEdit
                    }
                }}>

                <Flex flexDirection="column" gap={16}>

                    {props.survey.themes.map((t, i) => {
                        return (
                            <ThemedQuestionList
                                key={i}
                                survey={props.survey}
                                themes={props.survey.themes}
                                theme={t}
                                themeIndex={i}
                                allIndicators={allIndicators}
                                languageCode={props.languageCode}
                                    onSurveyUpdated={props.onSurveyUpdated}
                                />
                        );
                    })}
                </Flex>

                {surveyHelper.getAllSurveyQuestions(props.survey).length === 0 &&
                    <p>
                        Der er ikke oprettet spørgsmål for undersøgelsen
                    </p>
                }
            </PageSection>
        </DndContext>
    );
}